import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FiHome, FiFolder, FiFile, FiFileText, FiMessageSquare, FiSearch, FiCheckSquare, FiList, FiUsers, FiLogOut, FiSettings, FiShield, FiMenu, FiX, FiSmartphone, FiPackage, FiBarChart2, FiChevronDown, FiChevronRight, FiClock, FiArchive } from 'react-icons/fi';
import useAuthStore from '../store/authStore';
import MobileAuthModal from './MobileAuthModal';
import { adminAPI, supplierReconAPI, businessIntelligenceAPI } from '../lib/api';

const Layout = ({ children }) => {
  const location = useLocation();
  const { profile, logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileAuthOpen, setIsMobileAuthOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [supplierReconEnabled, setSupplierReconEnabled] = useState(false);
  const [businessIntelligenceEnabled, setBusinessIntelligenceEnabled] = useState(false);
  const [biGroups, setBiGroups] = useState([]);
  const [biNavExpanded, setBiNavExpanded] = useState(false);
  const [expandedBiGroups, setExpandedBiGroups] = useState(new Set());

  // Load company logo
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await adminAPI.getCompanyLogo();
        if (response.data?.logo_url) {
          setLogoUrl(response.data.logo_url);
        }
      } catch (error) {
        console.error('Failed to load logo:', error);
      }
    };
    loadLogo();
  }, []);

  // Load supplier recon enabled state
  useEffect(() => {
    supplierReconAPI.getConfig()
      .then(res => setSupplierReconEnabled(res.data?.enabled || false))
      .catch(() => {});
  }, []);

  // Load Business Intelligence enabled state
  useEffect(() => {
    businessIntelligenceAPI.getConfig()
      .then(res => setBusinessIntelligenceEnabled(res.data?.enabled || false))
      .catch(() => {});
  }, []);

  // Load Business Intelligence groups (for the per-group sidebar sub-navigation)
  useEffect(() => {
    if (!businessIntelligenceEnabled) return;
    businessIntelligenceAPI.getGroups()
      .then(res => setBiGroups(res.data?.groups || []))
      .catch(() => {});
  }, [businessIntelligenceEnabled]);

  // Auto-expand the Business Intelligence nav (and the relevant group) when landing on a BI page via a direct link
  useEffect(() => {
    if (location.pathname.startsWith('/business-intelligence')) {
      setBiNavExpanded(true);
    }
  }, [location.pathname]);

  const toggleBiGroup = (groupId) => {
    setExpandedBiGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const navigation = [
    { name: 'Dashboard', path: '/', icon: FiHome, roles: null, module: 'dashboard' },
    { name: 'Documents', path: '/documents', icon: FiFile, roles: null, module: 'documents' },
    { name: 'Folders', path: '/folders', icon: FiFolder, roles: null, module: 'folders' },
    { name: 'AI Assistant', path: '/chatbot', icon: FiMessageSquare, roles: null, module: 'ai_assistant' },
    { name: 'Search', path: '/search', icon: FiSearch, roles: null, module: 'search' },
    { name: 'Me To Action', path: '/me-to-action', icon: FiCheckSquare, roles: null, module: 'me_to_action' },
    ...(supplierReconEnabled ? [
      { name: 'Supplier Recon', path: '/supplier-recon', icon: FiPackage, roles: null, module: 'supplier_recon' },
      { name: 'Suppliers', path: '/suppliers', icon: FiUsers, roles: null, module: 'supplier_recon' },
    ] : []),
    { name: 'Audit Log', path: '/audit-log', icon: FiList, roles: ['admin'] },
    { name: 'Users', path: '/users', icon: FiUsers, roles: ['admin'] },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', path: '/admin/dashboard', icon: FiHome, roles: ['admin'] },
    { name: 'Security Events', path: '/admin/security', icon: FiShield, roles: ['admin'] },
    { name: 'System Settings', path: '/admin/settings', icon: FiSettings, roles: ['admin'] },
  ];

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const hasAccess = (roles) => {
    if (!roles) return true;
    return roles.includes(profile?.role);
  };

  // Per-user module restriction — admins always pass regardless of flag values (never lockable
  // out). A missing/undefined flag (e.g. before the migration backfills defaults) is treated as
  // allowed; only an explicit `false` restricts.
  const hasModuleAccess = (module) => {
    if (!module) return true;
    if (profile?.role === 'admin') return true;
    return profile?.[`access_${module}`] !== false;
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg text-gray-700 hover:bg-gray-100"
      >
        {isMobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`w-80 bg-white border-r border-gray-200 flex flex-col fixed h-screen z-40 transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Logo */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-center">
            <img
              src={logoUrl || "/images/alzu-logo.png"}
              alt="Company Logo"
              className="h-12 w-auto"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-hidden">
          {navigation.map((item) => {
            if (!hasAccess(item.roles) || !hasModuleAccess(item.module)) return null;

            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}

          {/* Business Intelligence — expandable: groups, each with Work in Progress / Archive */}
          {businessIntelligenceEnabled && hasModuleAccess('business_intelligence') && (
            <div>
              <div
                className={`flex items-center rounded-lg transition-colors ${
                  isActive('/business-intelligence') ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Link
                  to="/business-intelligence"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center flex-1 px-4 py-3 min-w-0"
                >
                  <FiBarChart2 className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span className="font-medium truncate">Business Intelligence</span>
                </Link>
                <button
                  onClick={() => setBiNavExpanded(prev => !prev)}
                  className="p-3 flex-shrink-0"
                  title={biNavExpanded ? 'Collapse' : 'Expand'}
                >
                  {biNavExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                </button>
              </div>

              {biNavExpanded && (
                <div className="ml-4 pl-3 border-l border-gray-200 space-y-0.5">
                  {biGroups.map(group => {
                    const groupExpanded = expandedBiGroups.has(group.id);
                    return (
                      <div key={group.id}>
                        <div className="flex items-center rounded-lg text-gray-600 hover:bg-gray-50">
                          <Link
                            to={`/business-intelligence?group=${encodeURIComponent(group.name)}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex-1 px-3 py-2 text-sm font-medium truncate min-w-0"
                          >
                            {group.name}
                          </Link>
                          <button
                            onClick={() => toggleBiGroup(group.id)}
                            className="p-2 flex-shrink-0"
                            title={groupExpanded ? 'Collapse' : 'Expand'}
                          >
                            {groupExpanded ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        {groupExpanded && (
                          <div className="ml-3 pl-3 border-l border-gray-100 space-y-0.5 mb-1">
                            {group.rfq_enabled && (
                              <Link
                                to={`/business-intelligence?group=${encodeURIComponent(group.name)}&view=rfq`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-lg"
                              >
                                <FiFileText className="w-3.5 h-3.5" />
                                RFQ
                              </Link>
                            )}
                            <Link
                              to={`/business-intelligence?group=${encodeURIComponent(group.name)}&view=wip`}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-lg"
                            >
                              <FiClock className="w-3.5 h-3.5" />
                              Work in Progress
                            </Link>
                            <Link
                              to={`/business-intelligence?group=${encodeURIComponent(group.name)}&view=archive`}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-lg"
                            >
                              <FiArchive className="w-3.5 h-3.5" />
                              Archive
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Admin Section */}
          {profile?.role === 'admin' && (
            <>
              <div className="pt-4 pb-2 px-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Administration
                </h3>
              </div>
              {adminNavigation.map((item) => {
                if (!hasAccess(item.roles)) return null;

                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                      active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          {/* Mobile Auth Button */}
          <button
            onClick={() => {
              setIsMobileAuthOpen(true);
              setIsMobileMenuOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors mb-3"
          >
            <FiSmartphone className="w-5 h-5" />
            <span className="font-medium">Link Mobile App</span>
          </button>

          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-medium text-gray-900">{profile?.full_name}</p>
              <p className="text-sm text-gray-500">{profile?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <FiLogOut className="w-5 h-5" />
            </button>
          </div>

          {/* Version Info */}
          <div className="text-center pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">V-Y2025-Q4-REF01</p>
          </div>
        </div>
      </div>

      {/* Mobile Auth Modal */}
      <MobileAuthModal
        isOpen={isMobileAuthOpen}
        onClose={() => setIsMobileAuthOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto lg:ml-80">
        <div className="max-w-7xl mx-auto p-4 lg:p-8 mt-16 lg:mt-0">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
